import {
  CompoundEntityRef,
  Entity,
  EntityMeta,
  GroupEntity,
  UserEntity,
  stringifyEntityRef,
  getCompoundEntityRef,
  parseEntityRef,
  ANNOTATION_ORIGIN_LOCATION,
  parseLocationRef,
  getEntitySourceLocation,
  ApiEntity,
} from '@backstage/catalog-model';
import {
  compare,
  getGitHubInfo,
  replace,
} from '@appfolio/backstage-plugin-utils-common';
import { DEFAULT_NAMESPACE } from './constants';
import { toSlug } from './entityName';
import { StructuredAnnotationsMetadataEntity } from './types';
import { assertError } from '@backstage/errors';
import z from 'zod/v3';

const getUrlPath = (
  basePath: string,
  ref: CompoundEntityRef,
  options?: {
    path?: string;
  },
) =>
  [basePath, ref.namespace, ref.kind, ref.name, options?.path]
    .filter(Boolean)
    .join('/')
    .replace(/\/{2,}/g, '/')
    .toLocaleLowerCase('en-US');

export type EntityKind =
  | 'domain'
  | 'component'
  | 'system'
  | 'group'
  | 'user'
  | 'api'
  | 'location'
  | 'template'
  | 'resource';

export const entityRefUtil = (self: CompoundEntityRef) => ({
  getCatalogUrlPath: () => getUrlPath('catalog', self),
  getDocsUrlPath: (options?: { path?: string }) =>
    getUrlPath('docs', self, options),
  isKind: (kind: EntityKind) => compare(self.kind, kind),
  is: (ref: CompoundEntityRef | string) => {
    if (typeof ref === 'string') {
      ref = parseEntityRef(ref, {
        defaultKind: self.kind,
        defaultNamespace: self.namespace,
      });
    }
    return (
      compare(self.kind, ref.kind) &&
      compare(self.namespace, ref.namespace) &&
      compare(self.name, ref.name)
    );
  },
  normalize: () =>
    Object.fromEntries(
      Object.entries(self).map(([key, value]) => [
        key.toLowerCase(),
        value.toLocaleLowerCase('en-US'),
      ]),
    ),
  toStringEntityRef: () => stringifyEntityRef(self),
});

const isKind = (entity: Entity, kind: EntityKind | EntityKind[]) =>
  typeof kind === 'string'
    ? compare(entity.kind, kind)
    : kind.some(k => compare(entity.kind, k));

const isType = (entity: Entity, type: string | string[]) =>
  typeof type === 'string'
    ? compare(entity.spec?.type?.toString(), type)
    : type.some(t => compare(entity.spec?.type?.toString(), t));

const getAnnotation = (entity: Entity, annotation: string) =>
  entity.metadata.annotations?.[annotation];

const getStructuredAnnotation = (entity: Entity, annotation: string) =>
  (entity as StructuredAnnotationsMetadataEntity).metadata
    .structuredAnnotations?.[annotation];

const getOwnerRef = (entity: Entity) =>
  entity.spec?.owner
    ? parseEntityRef(String(entity.spec.owner), {
        defaultKind: 'group',
        defaultNamespace: DEFAULT_NAMESPACE,
      })
    : getDefaultGroupRef();

export const entityUtil = (entity: Entity) => ({
  getAnnotation: (annotation: string) => getAnnotation(entity, annotation),
  getStructuredAnnotation: (annotation: string) =>
    getStructuredAnnotation(entity, annotation),
  getCatalogUrlPath: () => getUrlPath('catalog', getCompoundEntityRef(entity)),
  getDocsUrlPath: (options?: { path?: string }) =>
    getUrlPath('docs', getCompoundEntityRef(entity), options),
  getGitHubSourceLocationInfo: () => {
    try {
      const { type, target } = getEntitySourceLocation(entity);
      return type === 'url' ? getGitHubInfo(target) : null;
    } catch (e) {
      assertError(e);
      return null;
    }
  },
  getOwnerRef: () => getOwnerRef(entity),
  getDescription: () => entity.metadata.description,
  getTitle: () => entity.metadata.title,
  getLifecycle: () =>
    entity.spec?.lifecycle ? String(entity.spec.lifecycle) : undefined,
  getEmail: () =>
    isKind(entity, 'user')
      ? (entity as UserEntity).spec.profile?.email
      : undefined,
  getProfilePictureUrl: () => {
    if (!isKind(entity, 'user')) {
      return undefined;
    }
    const url = (entity as UserEntity).spec.profile?.picture;
    if (!url) {
      return undefined;
    }
    const { data: result } = z.string().url().safeParse(url);
    return result;
  },
  getApiSpec: () => {
    if (!isKind(entity, 'api')) {
      return undefined;
    }
    const api = entity as ApiEntity;
    return { type: api.spec.type, definition: api.spec.definition };
  },
  getName: () => entity.metadata.name,
  getLinks: () => entity.metadata.links ?? [],
  getTags: () =>
    Array.from(new Set(entity.metadata.tags ?? [])).map(t =>
      toSlug(t).toLocaleLowerCase('en-US'),
    ),
  getType: () => (entity.spec?.type ? entity.spec?.type.toString() : undefined),
  is: (ref: CompoundEntityRef | CompoundEntityRef[] | string) =>
    Array.isArray(ref)
      ? ref.some(r => entityRefUtil(getCompoundEntityRef(entity)).is(r))
      : entityRefUtil(getCompoundEntityRef(entity)).is(ref),
  isDocumented: () =>
    ['backstage.io/techdocs-ref', 'backstage.io/techdocs-entity'].some(
      a => typeof entity.metadata.annotations?.[a] !== 'undefined',
    ),
  isKind: (kind: EntityKind | EntityKind[]) => isKind(entity, kind),
  isComponentType: (type: string | string[]) =>
    isKind(entity, 'component') && isType(entity, type),
  isGroupType: (type: string | string[]) =>
    isKind(entity, 'group') && isType(entity, type),
  isType: (type: string | string[]) => isType(entity, type),
  isAnnotated: (annotation: string) =>
    typeof getAnnotation(entity, annotation) !== 'undefined',
  isStructurallyAnnotated: (annotation: string) =>
    typeof getStructuredAnnotation(entity, annotation) !== 'undefined',
  isAnnotatedWith: (annotation: string, value: string) =>
    getAnnotation(entity, annotation) === value,
  isStructurallyAnnotatedWith: (annotation: string, value: string) =>
    getStructuredAnnotation(entity, annotation)?.includes(value),
  isOriginLocationHostname: (hostname: string) => {
    const originLocationRef = getAnnotation(entity, ANNOTATION_ORIGIN_LOCATION);
    if (!originLocationRef) {
      return false;
    }
    const { type, target } = parseLocationRef(originLocationRef);
    if (type !== 'url') {
      return false;
    }
    try {
      const url = new URL(target);
      return compare(url.hostname, hostname);
    } catch {
      return false;
    }
  },
  isOwnedBy: (ref: CompoundEntityRef) =>
    entityRefUtil(getOwnerRef(entity)).is(ref),
  normalize: () => entityRefUtil(getCompoundEntityRef(entity)).normalize(),
  humanize: () => {
    if (isKind(entity, 'user')) {
      const e = entity as UserEntity;
      return (
        e.spec?.profile?.displayName ??
        entity.metadata.title ??
        stringifyEntityRef(entity)
      );
    }
    if (isKind(entity, 'group')) {
      const e = entity as GroupEntity;
      return (
        e.spec?.profile?.displayName ??
        entity.metadata.title ??
        stringifyEntityRef(entity)
      );
    }
    return entity.metadata.title ?? stringifyEntityRef(entity);
  },
  toStringEntityRef: () => stringifyEntityRef(entity),
  toCompoundEntityRef: () => getCompoundEntityRef(entity),
});

export const replaceWithEntityMetadataValues = (
  text: string,
  metadata: EntityMeta,
) =>
  replace(text, {
    name: metadata.title ?? metadata.name,
    description: metadata.description,
  });

export const isDocumented = (entity: Entity) =>
  ['backstage.io/techdocs-ref', 'backstage.io/techdocs-entity'].some(
    a => typeof entity.metadata.annotations?.[a] !== 'undefined',
  );

export const isUserByEmail = (entity: UserEntity, email: string) => {
  const [local, domain] = email.split('@');
  const normalizedLocal = local.split('+')[0];
  return compare(entity.spec.profile?.email, `${normalizedLocal}@${domain}`);
};

export const getDefaultGroupRef = (): CompoundEntityRef =>
  getProductDevelopmentGroupRef();

export const getProductDevelopmentGroupRef = (): CompoundEntityRef => ({
  kind: 'group',
  namespace: DEFAULT_NAMESPACE,
  name: 'appfolio-developers',
});
